/**
 * Générateur — Il modélise et résout des problèmes simples mettant en jeu les
 * notions de divisibilité et de nombre premier.
 * Attendu ID : 157 | Niveau : 4ème | Domaine : Divisibilité (desc 10, O21)
 *
 * Source doc prof : Ex.1-12 (division euclidienne, critères 3/9, vocabulaire
 * multiple/diviseur, multiples communs « rythmes », diviseurs communs « lots »,
 * premiers entre eux). Plan de cartes validé 2026-07-14, réduit le 2026-09-23 à
 * une seule carte :
 *
 *   Carte « lots » — Des bouquets tous identiques (qcm_unique + qcm_multiple +
 *   texte_trou_saisie)
 *     A = g·a roses et B = g·b tulipes, a et b sans diviseur commun autre que
 *     1 : les nombres de bouquets possibles sont les diviseurs de g
 *     supérieurs à 1, le plus grand est g. Non nommés (ni PGCD ni PPCM, hors
 *     programme de 4e).
 *
 * Tout par construction (A et B bâtis depuis leurs facteurs), revérifié par
 * l'énumération des diviseurs communs, throw sinon.
 *
 * Revue 2026-09-23 (vague 153-163), attendu de fin de 4e : « Il modélise et
 * résout des problèmes simples mettant en jeu les notions de divisibilité et de
 * nombre premier. » Exemple de réussite : « Un fleuriste doit réaliser des
 * bouquets tous identiques. Il dispose pour cela de 434 roses et 620 tulipes.
 * Quelles sont toutes les compositions de bouquets possibles ? »
 * (434 = 2 × 7 × 31, 620 = 2 × 2 × 5 × 31 ; diviseurs communs 1, 2, 31, 62).
 *   - SORTIES, servies ailleurs sous le plafond : « reste » (deux formats ;
 *     division euclidienne, attendus de fin de 5e, gen 1040 ; le reste affiché
 *     donnait la réponse du QCM dans 100 % des tirages), « criteres » (critères
 *     de 3 et de 9 : 5e, gen 700), « vocabulaire » (multiple et diviseur : 5e,
 *     gens 699 et 907 ; son distracteur « premiers entre eux »), « premiers »
 *     (« premiers entre eux » hors programme, clé « Non » et plus longue dans
 *     100 % des tirages).
 *   - SORTIE, mode auto, À ARBITRER : « rythmes » (deux formats, conjonction de
 *     phénomènes). L'exemple de réussite qui la borne est en 3e (les ampoules
 *     153 s et 187 s) et la carte 207 de 3e la porte ; la clé était « Oui »
 *     dans 69,5 % des tirages et « dans 0 jours » sortait dans 1,8 %. Repli si
 *     le prof la veut en 4e : des périodes de 12 jours au plus, dans les
 *     tables, jamais de décomposition, et le prénom à la place de « ils ».
 *   - « lots » recadrée sur le fleuriste, libellé « Résoudre un problème de
 *     bouquets identiques » ; trois contextes de même structure (le fleuriste,
 *     roses et tulipes, un tirage sur deux ; la confiseuse, caramels et
 *     sucettes ; la libraire, cahiers et stylos), personnages désignés par un
 *     nom commun, jamais par « il » ou « elle » seul. Les équipes, citées par
 *     les décisions, ne sont pas reprises : avec des nombres jusqu'à 1 000, des
 *     centaines d'élèves par sorte ne font pas une situation crédible.
 *     A = g·a, B = g·b, g produit d'un premier parmi 2, 3, 5, 7 et d'un
 *     premier de 11 à 31 (ou, pour le QCM à réponse unique, un seul premier
 *     de 11 à 31), a ≥ 2 et b ≥ 2 sans diviseur commun, donc A ≠ B et aucun
 *     ne divise l'autre ;
 *     A, B ≤ 1 000 ; au moins deux bouquets. Avant : A, B et le nombre de lots
 *     tirés indépendamment, la clé « Non » dans 95,8 % des tirages, et 12,3 %
 *     de A = B, 31,3 % de A multiple de B dans la carte à trous.
 *   - lots_qcm_unique (clé gardée, le QCM dépouillable de l'attendu) :
 *     « Combien de bouquets identiques le fleuriste peut-il faire en utilisant
 *     toutes les fleurs ? », une seule option est un diviseur commun ;
 *     distracteurs divise_un_seul (A seulement, B seulement) et
 *     diviseur_de_la_somme ; le choix bouche-trou « On ne peut pas le
 *     savoir. » sort. Contrat exporté. Ajouts du correcteur : options entre
 *     2 et le plus petit des deux nombres, A et B exclus ; rang de la clé
 *     tiré uniformément parmi les quatre (20 / 30 / 30 / 20 % mesurés, contre
 *     83 % aux deux rangs du milieu avec un simple encadrement).
 *   - lots_qcm_multiple NOUVEAU, mode auto, À ARBITRER : les trois nombres de
 *     bouquets possibles (s, P et s·P) à sélectionner parmi six, c'est
 *     l'exemple de réussite (« toutes les compositions »). La consigne ANNONCE
 *     le nombre de réponses (« Sélectionne les 3 nombres de bouquets
 *     possibles… ») comme l'exige FORMATS_TANOED §2 ; les décisions écrivaient
 *     « Sélectionne tous les nombres… », sans le nombre : écart du correcteur,
 *     à trancher par le prof. Les décisions annonçaient aussi de 2 à 6 bonnes
 *     réponses : avec g = s·P il y en a toujours exactement trois (s, P, s·P),
 *     comme dans l'exemple (2, 31, 62) ; le correcteur a gardé la définition
 *     de g plutôt que ce compte.
 *   - lots_texte_trou_saisie : deux lignes KaTeX, la composition pour un
 *     nombre de bouquets donné, puis « Au plus … bouquets, avec … roses et …
 *     tulipes chacun » (le plus grand nombre seulement en dernière ligne,
 *     jamais seul : seul, c'est la carte 207 de 3e) ; chaque trou ancré par
 *     son mot, aucune paire commutative ; la phrase qui répétait l'énoncé sort.
 *   - Rappel brouillon IMPÉRATIF sur les trois formats, « ✏️ Pose tes calculs
 *     au brouillon pour résoudre. » (attendu de problèmes), après l'énoncé,
 *     juste avant la question.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { pgcd } from './premiers';

const ATTENDU_ID = 157;

const L_LOTS = 'Résoudre un problème de bouquets identiques';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF sur les trois formats,
// tournure des attendus de problèmes (149). Place : après l'énoncé du problème,
// juste avant la question ; aucune méthode soufflée.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon pour résoudre.</p>';

// Phrase à trous en lignes KaTeX globales : le français passe en \text{}.
const T = (s) => `\\text{${s}}`;

const PETITS_PREMIERS = [2, 3, 5, 7];
const GRANDS_PREMIERS = [11, 13, 17, 19, 23, 29, 31];
const VALEUR_MAX = 1000;

// ─── Contextes (même structure que le fleuriste de l'exemple) ───────────────
// Le personnage est toujours désigné par un nom commun ; « peut-il » et
// « peut-elle » ne sont que l'inversion de la question, sujet nommé.
const CONTEXTES = [
    {
        id: 'fleuriste',
        intro: (A, B) => `Un fleuriste doit réaliser au moins deux bouquets, tous identiques, avec ${A} roses et ${B} tulipes.`,
        question: 'Combien de bouquets identiques le fleuriste peut-il faire en utilisant toutes les fleurs ?',
        selection: (k) => `Sélectionne les ${k} nombres de bouquets possibles en utilisant toutes les fleurs.`,
        consigneTrous: 'Complète la composition des bouquets, en utilisant toutes les fleurs.',
        contenant: 'bouquets', o1: 'roses', o2: 'tulipes',
    },
    {
        id: 'confiseuse',
        intro: (A, B) => `Une confiseuse doit préparer au moins deux sachets, tous identiques, avec ${A} caramels et ${B} sucettes.`,
        question: 'Combien de sachets identiques la confiseuse peut-elle préparer en utilisant tous les bonbons ?',
        selection: (k) => `Sélectionne les ${k} nombres de sachets possibles en utilisant tous les bonbons.`,
        consigneTrous: 'Complète la composition des sachets, en utilisant tous les bonbons.',
        contenant: 'sachets', o1: 'caramels', o2: 'sucettes',
    },
    {
        id: 'libraire',
        intro: (A, B) => `Une libraire doit préparer au moins deux lots, tous identiques, avec ${A} cahiers et ${B} stylos.`,
        question: 'Combien de lots identiques la libraire peut-elle préparer en utilisant tous les articles ?',
        selection: (k) => `Sélectionne les ${k} nombres de lots possibles en utilisant tous les articles.`,
        consigneTrous: 'Complète la composition des lots, en utilisant tous les articles.',
        contenant: 'lots', o1: 'cahiers', o2: 'stylos',
    },
];
// Le fleuriste (l'exemple de réussite) un tirage sur deux.
const tirerContexte = () => (randInt(0, 1) === 0 ? CONTEXTES[0] : randomChoice(CONTEXTES.slice(1)));

// ─── Second chemin : diviseurs par énumération ───────────────────────────────
const diviseurs = (n) => {
    const out = [];
    for (let d = 1; d <= n; d++) if (n % d === 0) out.push(d);
    return out;
};
const divise = (d, n) => n % d === 0;

// ─── Tirage du problème ──────────────────────────────────────────────────────
// A = g·a, B = g·b, a ≥ 2, b ≥ 2, a ≠ b, pgcd(a, b) = 1 ; g = s·P (s parmi 2,
// 3, 5, 7 ; P de 11 à 31) ou g = P seul. Les nombres de bouquets possibles (au
// moins deux) sont alors exactement les diviseurs de g supérieurs à 1.
const tirerProbleme = (avecPetit) => {
    for (let essai = 0; essai < 80; essai++) {
        const P = randomChoice(GRANDS_PREMIERS);
        const s = avecPetit ? randomChoice(PETITS_PREMIERS) : 1;
        const g = s * P;
        const aMax = Math.floor(VALEUR_MAX / g);
        if (aMax < 3) continue;
        const a = randInt(2, aMax);
        const b = randInt(2, aMax);
        if (a === b || pgcd(a, b) !== 1) continue;
        return { g, s, P, a, b, A: g * a, B: g * b };
    }
    return { ...REPLIS[avecPetit ? 'avecPetit' : 'sansPetit'].pb };
};

// Replis déterministes, vérifiés à chaque usage : l'exemple de réussite
// (434 = 62 × 7 roses, 620 = 62 × 10 tulipes) et, pour g = P seul, les mêmes
// cofacteurs sur 31 (217 et 310). Un distracteur par famille, choisi à la main :
// 14 et 7 ne divisent que A, 20 et 10 que B, 17 divise A + B (1 054 et 527)
// sans diviser A ni B.
const REPLIS = {
    avecPetit: {
        pb: { g: 62, s: 2, P: 31, a: 7, b: 10, A: 434, B: 620 },
        choisis: { divise_A_seulement: 14, divise_B_seulement: 20, diviseur_de_la_somme: 17 },
    },
    sansPetit: {
        pb: { g: 31, s: 1, P: 31, a: 7, b: 10, A: 217, B: 310 },
        choisis: { divise_A_seulement: 7, divise_B_seulement: 10, diviseur_de_la_somme: 17 },
    },
};

// Vérifie qu'un tirage a exactement les nombres de bouquets attendus.
const verifieProbleme = ({ A, B, g }) => {
    const communs = diviseurs(A).filter((d) => d > 1 && divise(d, B));
    const attendus = diviseurs(g).filter((d) => d > 1);
    if (communs.join(',') !== attendus.join(',') || A === B || divise(A, B) || divise(B, A)
        || A > VALEUR_MAX || B > VALEUR_MAX) {
        throw new Error(`gen_157 : tirage ${A} / ${B} incohérent`);
    }
    return communs;
};

// Candidats par famille d'erreur, tous au moins 2 et strictement sous le plus
// petit des deux nombres (on ne fait pas plus de bouquets qu'il n'y a de
// tulipes, et « 121 bouquets » pour 121 tulipes ne tente personne).
const candidatsFamilles = ({ A, B }) => {
    const plafond = Math.min(A, B);
    const dans = (d) => d >= 2 && d < plafond;
    return {
        divise_A_seulement: diviseurs(A).filter((d) => dans(d) && !divise(d, B)),
        divise_B_seulement: diviseurs(B).filter((d) => dans(d) && !divise(d, A)),
        diviseur_de_la_somme: diviseurs(A + B).filter((d) => dans(d) && !divise(d, A) && !divise(d, B)),
    };
};

// Un candidat par famille, du côté visé par rapport à la clé de référence
// (« dessous », « dessus » ou « hasard ») quand les candidats le permettent.
const choisirDistracteurs = (cands, reference, pris, cotes) => {
    const choisis = {};
    Object.keys(cands).forEach((fam, i) => {
        const libres = cands[fam].filter((d) => !pris.includes(d) && !Object.values(choisis).includes(d));
        const cote = cotes[i];
        const vises = cote === 'dessous' ? libres.filter((d) => d < reference)
            : cote === 'dessus' ? libres.filter((d) => d > reference) : libres;
        choisis[fam] = randomChoice(vises.length ? vises : libres);
    });
    return choisis;
};

// Code d'erreur publié (les deux côtés de divise_un_seul gardent leur nom).
const FAMILLE_PUBLIEE = {
    divise_A_seulement: 'divise_un_seul',
    divise_B_seulement: 'divise_un_seul',
    diviseur_de_la_somme: 'diviseur_de_la_somme',
};

// Côtés visés. QCM unique : le rang de la clé parmi les quatre options est tiré
// uniformément (0 à 3 distracteurs au-dessous), pour qu'elle ne soit ni
// toujours au milieu ni toujours à un bout. QCM multiple : autour de la clé du
// milieu (P), un distracteur au-dessous, un au-dessus, un au hasard.
const cotesUnique = () => {
    const dessous = randInt(0, 3);
    return shuffle([...Array(dessous).fill('dessous'), ...Array(3 - dessous).fill('dessus')]);
};
const cotesMultiple = () => shuffle(['dessous', 'dessus', 'hasard']);

// Tirage complet : problème + un candidat par famille, re-tiré (borné) tant
// qu'une famille est vide ; repli sur l'exemple de réussite, vérifié.
const tirerAvecDistracteurs = (avecPetit, cleDe, cotesDe) => {
    for (let essai = 0; essai < 40; essai++) {
        const pb = tirerProbleme(avecPetit);
        const communs = verifieProbleme(pb);
        const cands = candidatsFamilles(pb);
        if (Object.values(cands).some((l) => !l.length)) continue;
        const cles = cleDe(pb, communs);
        const reference = cles[Math.floor(cles.length / 2)];
        const choisis = choisirDistracteurs(cands, reference, cles, cotesDe());
        if (Object.values(choisis).some((d) => d === undefined)) continue;
        return { pb, cles, choisis };
    }
    const repli = REPLIS[avecPetit ? 'avecPetit' : 'sansPetit'];
    const pb = { ...repli.pb };
    return { pb, cles: cleDe(pb, verifieProbleme(pb)), choisis: { ...repli.choisis } };
};

// Vérifie les options : les clés divisent A et B, aucun distracteur ne divise
// les deux ; toutes distinctes.
const verifieOptions = ({ A, B }, cles, choisis) => {
    const distr = Object.values(choisis);
    if (!cles.every((d) => d >= 2 && divise(d, A) && divise(d, B))
        || distr.some((d) => divise(d, A) && divise(d, B))
        || !divise(choisis.divise_A_seulement, A) || divise(choisis.divise_A_seulement, B)
        || !divise(choisis.divise_B_seulement, B) || divise(choisis.divise_B_seulement, A)
        || !divise(choisis.diviseur_de_la_somme, A + B)
        || new Set([...cles, ...distr]).size !== cles.length + distr.length) {
        throw new Error(`gen_157 : options incohérentes pour ${A} / ${B}`);
    }
};

// ═════════ Contrat des distracteurs (donnée pure, jamais lue à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    lots_qcm_unique: {
        support: 'A = g·a et B = g·b (a, b ≥ 2 sans diviseur commun, A, B ≤ 1 000), g = s·P (s parmi 2, 3, 5, 7 ; P premier de 11 à 31) un tirage sur deux, g = P sinon ; une seule option est un nombre de bouquets possible : P ou g (jamais le petit premier s, visible aux critères de divisibilité)',
        familles: {
            divise_un_seul: 'un diviseur de A qui ne divise pas B, ou de B qui ne divise pas A : il manquerait des fleurs d\'une sorte (un distracteur de chaque côté)',
            diviseur_de_la_somme: 'un diviseur de A + B qui ne divise ni A ni B : le nombre total de fleurs partagé au lieu de chaque sorte séparément',
        },
        supprimees: {
            on_ne_peut_pas_le_savoir: 'choix bouche-trou jamais juste, sans erreur derrière',
            oui_non: 'l\'ancien « Peut-on faire exactement L lots ? » : clé « Non » dans 95,8 % des tirages',
        },
        garantie: 'quatre choix : la clé et un distracteur par famille (A seulement, B seulement, somme), tous au moins 2 et strictement sous le plus petit des deux nombres ; exactement une option divise A et B, vérifié par l\'énumération des diviseurs, throw sinon ; famille vide → re-tirage borné du problème, repli sur l\'exemple de réussite (434, 620 : clé 31 ou 62, distracteurs 14, 20, 17). Rang de la clé parmi les quatre tiré uniformément (nombre de distracteurs visés au-dessous de 0 à 3), tenu quand les candidats le permettent',
    },
    lots_qcm_multiple: {
        support: 'mêmes nombres avec g = s·P : trois nombres de bouquets possibles, s, P et s·P, parmi six choix',
        familles: {
            divise_un_seul: 'comme au QCM unique, un de chaque côté',
            diviseur_de_la_somme: 'comme au QCM unique',
        },
        supprimees: {},
        garantie: 'six choix : les trois clés (tous les diviseurs communs supérieurs à 1, recomptés par énumération) et un distracteur par famille ; aucun distracteur ne divise A et B ; la consigne annonce le nombre de réponses (FORMATS_TANOED §2)',
    },
};

// ═════════ QCM à réponse unique ═════════

const genLotsQcm = () => {
    const ctx = tirerContexte();
    const avecPetit = randInt(0, 1) === 0;
    // Clé : le grand premier P, ou g = s·P quand il y a un petit premier (jamais s
    // seul : il se lit aux critères de 2, 3 ou 5 sans chercher le premier commun).
    const { pb, cles, choisis } = tirerAvecDistracteurs(avecPetit,
        (p) => [avecPetit ? randomChoice([p.P, p.g]) : p.P], cotesUnique);
    verifieOptions(pb, cles, choisis);
    const cle = cles[0];
    const valeurs = [cle, ...Object.values(choisis)];
    const payload = makeQcm(valeurs.map(String), String(cle),
        `<p>${ctx.intro(pb.A, pb.B)}</p>`
        + RAPPEL_BROUILLON
        + `<p><b>${ctx.question}</b></p>`,
        opts(L_LOTS));
    payload._meta.params = {
        contexte: ctx.id, A: pb.A, B: pb.B, g: pb.g, cle,
        distracteurs: Object.keys(choisis).map((f) => FAMILLE_PUBLIEE[f]),
    };
    return payload;
};

// ═════════ QCM à réponses multiples ═════════

const genLotsQcmMultiple = () => {
    const ctx = tirerContexte();
    const { pb, cles, choisis } = tirerAvecDistracteurs(true, (p, communs) => communs, cotesMultiple);
    verifieOptions(pb, cles, choisis);
    if (cles.length !== 3) throw new Error('gen_157 : trois nombres de bouquets attendus');
    const choix = shuffle([...cles, ...Object.values(choisis)]).map(String);
    const payload = makeQcm(choix, cles.map(String),
        `<p>${ctx.intro(pb.A, pb.B)}</p>`
        + RAPPEL_BROUILLON
        + `<p><b>${ctx.selection(cles.length)}</b></p>`,
        opts(L_LOTS), true);
    payload._meta.params = {
        contexte: ctx.id, A: pb.A, B: pb.B, g: pb.g, cles,
        distracteurs: Object.keys(choisis).map((f) => FAMILLE_PUBLIEE[f]),
    };
    return payload;
};

// ═════════ Texte à trous : la composition des bouquets ═════════

const genLotsTrous = () => {
    const ctx = tirerContexte();
    const pb = tirerProbleme(true);
    verifieProbleme(pb);
    // Un nombre de bouquets donné (s ou P, jamais le plus grand), puis le plus
    // grand, en dernière ligne.
    const d = randomChoice([pb.s, pb.P]);
    const ligne1 = `$${T(`Pour ${d} ${ctx.contenant} : `)}{{${pb.A / d}}}${T(` ${ctx.o1} et `)}{{${pb.B / d}}}${T(` ${ctx.o2} chacun`)}$`;
    const ligne2 = `$${T('Au plus ')}{{${pb.g}}}${T(` ${ctx.contenant}, avec `)}{{${pb.a}}}${T(` ${ctx.o1} et `)}{{${pb.b}}}${T(` ${ctx.o2} chacun`)}$`;
    if (!divise(d, pb.A) || !divise(d, pb.B) || pb.g * pb.a !== pb.A || pb.g * pb.b !== pb.B) {
        throw new Error('gen_157 trous : composition incohérente');
    }
    const payload = makeTexteTrou(`${ligne1}\n${ligne2}`,
        `<p>${ctx.intro(pb.A, pb.B)}</p>`
        + RAPPEL_BROUILLON
        + `<p><b>${ctx.consigneTrous}</b></p>`,
        { ...opts(L_LOTS), mode: 'saisie' });
    payload._meta.params = { contexte: ctx.id, A: pb.A, B: pb.B, g: pb.g, d };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    lots_qcm_unique: genLotsQcm,
    lots_qcm_multiple: genLotsQcmMultiple,
    lots_texte_trou_saisie: genLotsTrous,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', qcm_multiple: 'QCM Multi',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lots', label: L_LOTS, formatsList: buildFormats('lots') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
